import React, { useState } from 'react';
import axios from 'axios';
import { RISK_OPTIONS } from './constants';
import { ReviewComment, RiskLevel } from './types';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

/**
 * Editing a comment's text, risk level, suggested fix and replacement text.
 *
 * SHARED ON PURPOSE (2026-08-26). This was the reviewer card's private
 * business until the decision maker gained the same affordance — the backend
 * has allowed the DM to edit anyone's comment since the route shipped, but
 * DecisionCommentCard had no Edit button, so the rule existed only on one
 * side. Copying the form into the DM's card would have made it the third
 * edit form in this module, and the last time two of them existed they drifted
 * (see the two-viewer note in DocumentViewer). One component, two callers.
 *
 * The field shapes follow spec §8: only a FINDING ROOT carries severity and
 * suggested fix, and replacement text needs a text selection to apply to. A
 * consultation or a reply that gained those fields through the edit form would
 * be a different kind of record than the one that was written.
 */

interface CommentEditFormProps {
  comment: ReviewComment;
  getAuthHeaders: () => Promise<Record<string, string>>;
  /** Called after a successful save so the host can refetch. */
  onSaved?: () => void;
  onCancel: () => void;
  /**
   * Host decides (the gating idiom): only the comment's AUTHOR deletes —
   * the DM may edit but not delete (Fredrik, 2026-09-02). The backend
   * enforces the same rule plus the thread/disposition guards; this prop
   * only controls whether the button renders.
   */
  canDelete?: boolean;
}

const CommentEditForm: React.FC<CommentEditFormProps> = ({
  comment, getAuthHeaders, onSaved, onCancel, canDelete = false
}) => {
  const isConsultation = (comment.kind || 'finding') === 'consultation';
  const isRoot = !comment.parentComment;
  const carriesRisk = !isConsultation && isRoot;

  const [issue, setIssue] = useState(comment.issue || '');
  const [severity, setSeverity] = useState<RiskLevel>((comment.severity as RiskLevel) || 'minor');
  const [fix, setFix] = useState(comment.suggestedFix || '');
  const [proposed, setProposed] = useState((comment as any).proposedText || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!issue.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const body: Record<string, unknown> = { issue: issue.trim() };
      if (carriesRisk) {
        body.severity = severity;
        body.suggestedFix = fix;
        if (comment.textSelection) body.proposedText = proposed;
      }
      await axios.put(`${backendUrl}/api/content-review/comments/${comment._id}`, body, { headers });
      onSaved?.();
      onCancel();
    } catch (err: any) {
      // The backend refuses anyone who is neither the author nor a decision
      // maker. Say what it said rather than failing silently — a refused edit
      // that looks like a slow one is the shape that gets reported as "the
      // save button does nothing".
      setError(err.response?.data?.error || 'Could not save the edit.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="comment-edit-form">
      <div className="review-form-group">
        <textarea
          value={issue}
          onChange={e => setIssue(e.target.value)}
          rows={3}
          data-testid="comment-edit-issue"
        />
      </div>

      {carriesRisk && (
        <div className="review-form-group comment-edit-risk">
          <label>
            Risk level{' '}
            <select value={severity} onChange={e => setSeverity(e.target.value as RiskLevel)}>
              {RISK_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <input
            value={fix}
            onChange={e => setFix(e.target.value)}
            placeholder="Suggested fix (advice)"
          />
        </div>
      )}

      {carriesRisk && comment.textSelection && (
        <div className="review-form-group">
          <input
            value={proposed}
            onChange={e => setProposed(e.target.value)}
            placeholder="Replacement text — applied verbatim in place of the quoted selection"
            data-testid="comment-edit-proposed"
          />
          <p className="review-form-hint">
            Applied only if this exact selection is still present in the file.
          </p>
        </div>
      )}

      {error && <p className="rail-error">{error}</p>}

      <div className="comment-reply-actions">
        <button
          className="review-btn review-btn-primary rail-btn"
          onClick={save}
          disabled={!issue.trim() || saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button className="comment-reply-open rail-action-muted" onClick={onCancel}>
          Cancel
        </button>
        {canDelete && (
          <button
            className="comment-reply-open rail-action-muted"
            style={{ marginLeft: 'auto', color: '#b91c1c' }}
            disabled={saving}
            onClick={async () => {
              if (!window.confirm('Delete this comment? This cannot be undone.')) return;
              setSaving(true);
              setError(null);
              try {
                const headers = await getAuthHeaders();
                await axios.delete(`${backendUrl}/api/content-review/comments/${comment._id}`, { headers });
                onSaved?.();
                onCancel();
              } catch (err: any) {
                setError(err.response?.data?.error || 'Failed to delete');
              } finally {
                setSaving(false);
              }
            }}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
};

export default CommentEditForm;
